import { MS_PER_PIXEL, worldToScreenX } from "../timeline/book/BookLayout";
import { isPhoneStage } from "../timeline/view/CameraInsets/insets";
import type { TimelineVertex } from "./vertex-primitives";
import { pushWorldLine, type WorldView } from "./world-to-canvas";

const BASE_GRID_CELL_PX = 48;
const GRID_MAJOR_EVERY = 5;
const MAX_GRID_LINES = 90;
/** Smallest half-thickness on screen, so zooming out does not erase the grid. */
const MIN_SCREEN_HALF_PX = 0.75;

/** A small screen has less room, so the grid steps back and leaves it to the story. */
const PHONE_GRID_BRIGHTNESS = 0.45;

let dim = 1;

function gridBrightness(): number {
  const { innerWidth = Infinity, innerHeight = Infinity } = globalThis as {
    innerWidth?: number;
    innerHeight?: number;
  };
  return isPhoneStage(innerWidth, innerHeight) ? PHONE_GRID_BRIGHTNESS : 1;
}

export function appendTimelineGrid(
  verts: TimelineVertex[],
  scrollPx: number,
  axisY: number,
  view: WorldView,
): void {
  dim = gridBrightness();
  const { margin, cellPx } = gridLayout(view);
  const span = visibleSpan(view, margin);
  const stroke = gridStroke(view.zoom);
  appendVerticalGrid(verts, scrollPx, span, cellPx, view, stroke);
  appendHorizontalGrid(verts, axisY, span, cellPx, view, stroke, -scrollPx);
}

function gridStroke(zoom: number): number {
  return Math.max(1, MIN_SCREEN_HALF_PX / Math.max(zoom, 1e-6));
}

function gridLayout(view: WorldView): { margin: number; cellPx: number } {
  const margin = BASE_GRID_CELL_PX * 4;
  const visibleW = view.canvasW / view.zoom;
  const visibleH = view.canvasH / view.zoom;
  const span = Math.max(visibleW, visibleH) + margin * 2;
  const raw = Math.max(BASE_GRID_CELL_PX, span / MAX_GRID_LINES);
  const cellPx = Math.round(raw / 12) * 12;
  return { margin, cellPx: Math.max(BASE_GRID_CELL_PX, cellPx) };
}

interface VisibleSpan {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function visibleSpan(view: WorldView, margin: number): VisibleSpan {
  return {
    left: view.originX - margin,
    right: view.originX + view.canvasW / view.zoom + margin,
    top: view.originY - margin,
    bottom: view.originY + view.canvasH / view.zoom + margin,
  };
}

function appendVerticalGrid(
  verts: TimelineVertex[],
  scrollPx: number,
  span: VisibleSpan,
  cellPx: number,
  view: WorldView,
  stroke: number,
): void {
  const cellMs = cellPx * MS_PER_PIXEL;
  const viewLeft = span.left;
  const viewRight = span.right;
  const worldStartMs = (scrollPx + viewLeft) * MS_PER_PIXEL;
  const worldEndMs = (scrollPx + viewRight) * MS_PER_PIXEL;
  const firstIndex = Math.floor(worldStartMs / cellMs);
  const lastIndex = Math.ceil(worldEndMs / cellMs);

  for (let index = firstIndex; index <= lastIndex; index++) {
    const worldMs = index * cellMs;
    const x = worldToScreenX(worldMs, scrollPx);
    const major = index % GRID_MAJOR_EVERY === 0;
    if (major) {
      pushWorldLine(
        verts,
        x,
        span.top,
        x,
        span.bottom,
        view,
        0.48 * dim,
        0.5 * dim,
        0.54 * dim,
        0.32,
        stroke,
      );
    } else {
      pushWorldLine(
        verts,
        x,
        span.top,
        x,
        span.bottom,
        view,
        0.32 * dim,
        0.34 * dim,
        0.38 * dim,
        0.16,
        stroke,
      );
    }
  }
}

function appendHorizontalGrid(
  verts: TimelineVertex[],
  axisY: number,
  span: VisibleSpan,
  cellPx: number,
  view: WorldView,
  stroke: number,
  railStartX: number,
): void {
  const startRow = Math.floor(span.top / cellPx);
  const endRow = Math.ceil(span.bottom / cellPx);

  for (let row = startRow; row <= endRow; row++) {
    const y = row * cellPx;
    const major = row % GRID_MAJOR_EVERY === 0;
    if (Math.abs(y - axisY) < 11) {
      pushGridH(verts, span.left, railStartX, y, view, stroke, major);
      pushGridH(verts, view.worldW, span.right, y, view, stroke, major);
      continue;
    }
    pushGridH(verts, span.left, span.right, y, view, stroke, major);
  }
}

function pushGridH(
  verts: TimelineVertex[],
  x1: number,
  x2: number,
  y: number,
  view: WorldView,
  stroke: number,
  major: boolean,
): void {
  if (x2 <= x1) {
    return;
  }
  pushWorldLine(
    verts,
    x1,
    y,
    x2,
    y,
    view,
    major ? 0.48 : 0.28,
    major ? 0.5 : 0.3,
    major ? 0.54 : 0.34,
    major ? 0.26 : 0.12,
    stroke,
  );
}
